import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as dashboardService from '../../services/dashboardService';

// Define types for dashboard data
interface CompanyData {
  id: number;
  name: string;
  siret: string;
  address?: string;
  postalCode?: string;
  city?: string;
  departement?: string;
  nafCodes?: string[];
}

interface Stats {
  totalAdherents: number;
  newAdherentsLastMonth: number;
  newAdherentsLastWeek: number;
  totalImportFiles: number;
  recentImportFiles: number;
  percentChange: number;
}

interface DashboardStats {
  company: CompanyData;
  stats: Stats;
}

interface Adherent {
  id: number;
  siret: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  createdAt: string;
  bigData: {
    uniteLegale: any;
    etablissement: any;
  };
}

// New interface for adherent map data
export interface AdherentMapData extends Adherent {
  locationData: {
    id: string;
    adresse: string;
    commune: string;
    codePostal: string;
    locauxCount: number;
    numeroVoirie: string;
    libelleVoie: string;
    codeInsee: string;
    codeDepartement: string;
  };
}

interface DistributionItem {
  code: string;
  label: string;
  count: number;
}

interface Distributions {
  departments: DistributionItem[];
  nafCodes: DistributionItem[];
  categoriesJuridiques: DistributionItem[];
}

interface DashboardState {
  stats: DashboardStats | null;
  recentAdherents: Adherent[];
  distributions: Distributions | null;
  mapAdherents: AdherentMapData[];
  loadingStats: boolean;
  loadingAdherents: boolean;
  loadingDistributions: boolean;
  loadingMapAdherents: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  stats: null,
  recentAdherents: [],
  distributions: null,
  mapAdherents: [],
  loadingStats: false,
  loadingAdherents: false,
  loadingDistributions: false,
  loadingMapAdherents: false,
  error: null,
};

// Define async thunks
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      return await dashboardService.getDashboardStats();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des statistiques'
      );
    }
  }
);

export const fetchRecentAdherents = createAsyncThunk(
  'dashboard/fetchRecentAdherents',
  async (limit: number = 5, { rejectWithValue }) => {
    try {
      return await dashboardService.getRecentAdherents(limit);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des adhérents récents'
      );
    }
  }
);

export const fetchDistributions = createAsyncThunk(
  'dashboard/fetchDistributions',
  async (_, { rejectWithValue }) => {
    try {
      return await dashboardService.getDistributions();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des distributions'
      );
    }
  }
);

export const fetchAdherentsForMap = createAsyncThunk(
  'dashboard/fetchAdherentsForMap',
  async (_, { rejectWithValue }) => {
    try {
      return await dashboardService.getAdherentsForMap();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des adhérents pour la carte'
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Stats cases
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loadingStats = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loadingStats = false;
        state.error = action.payload as string;
      })
      
      // Recent adherents cases
      .addCase(fetchRecentAdherents.pending, (state) => {
        state.loadingAdherents = true;
        state.error = null;
      })
      .addCase(fetchRecentAdherents.fulfilled, (state, action) => {
        state.loadingAdherents = false;
        state.recentAdherents = action.payload;
      })
      .addCase(fetchRecentAdherents.rejected, (state, action) => {
        state.loadingAdherents = false;
        state.error = action.payload as string;
      })
      
      // Distributions cases
      .addCase(fetchDistributions.pending, (state) => {
        state.loadingDistributions = true;
        state.error = null;
      })
      .addCase(fetchDistributions.fulfilled, (state, action) => {
        state.loadingDistributions = false;
        state.distributions = action.payload;
      })
      .addCase(fetchDistributions.rejected, (state, action) => {
        state.loadingDistributions = false;
        state.error = action.payload as string;
      })
      
      // Map adherents cases
      .addCase(fetchAdherentsForMap.pending, (state) => {
        state.loadingMapAdherents = true;
        state.error = null;
      })
      .addCase(fetchAdherentsForMap.fulfilled, (state, action) => {
        state.loadingMapAdherents = false;
        state.mapAdherents = action.payload;
      })
      .addCase(fetchAdherentsForMap.rejected, (state, action) => {
        state.loadingMapAdherents = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboardErrors } = dashboardSlice.actions;
export default dashboardSlice.reducer; 